import React, { useState, useEffect, useCallback } from 'react';
import { overrideAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  CreditCard, Search, Filter, ChevronLeft, ChevronRight, Loader2,
  Plus, ArrowUpDown, CheckCircle2, Clock, XCircle, Send, Info, Trash2,
} from 'lucide-react';

const PAYMENT_METHODS = ['Direct Deposit', 'Check'];
const PAYMENT_STATUSES = ['Scheduled', 'Sent', 'Paid', 'Failed', 'Voided'];

const STATUS_COLORS = {
  Scheduled: 'bg-slate-100 text-slate-700',
  Sent: 'bg-sky-100 text-sky-700',
  Paid: 'bg-emerald-100 text-emerald-700',
  Failed: 'bg-red-100 text-red-700',
  Voided: 'bg-gray-100 text-gray-700',
};

const STATUS_ICONS = {
  Scheduled: <Clock size={12} />,
  Sent: <Send size={12} />,
  Paid: <CheckCircle2 size={12} />,
  Failed: <XCircle size={12} />,
  Voided: <XCircle size={12} />,
};

const fmtMoney = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '$0.00';
  return n < 0 ? `-$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

export default function PaymentsPage() {
  const { user } = useAuth();
  const canManage = isKRRole(user);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({ status: 'all', statement_month: 'all' });
  const [showFilters, setShowFilters] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);

  // Create payment dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ statement_id: '', payment_date: '', payment_method: 'Direct Deposit', amount: '', reference_number: '', notes: '' });
  const [creating, setCreating] = useState(false);
  const [generatedStatements, setGeneratedStatements] = useState([]);
  const [stmtSearch, setStmtSearch] = useState('');
  const [loadingStmts, setLoadingStmts] = useState(false);
  const [selectedStmt, setSelectedStmt] = useState(null);

  // Update status dialog
  const [statusDialog, setStatusDialog] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, page_size: pageSize,
        status: filters.status !== 'all' ? filters.status : undefined,
        statement_month: filters.statement_month !== 'all' ? filters.statement_month : undefined,
      };
      const res = await overrideAPI.payments.list(params);
      let data = res.data.items || [];
      // Filter by search client-side
      if (debouncedSearch) {
        data = data.filter(p => p.payee_name?.toLowerCase().includes(debouncedSearch.toLowerCase()));
      }
      setItems(data);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load payments');
    }
    setLoading(false);
  }, [page, pageSize, debouncedSearch, filters]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load available months
  useEffect(() => {
    overrideAPI.months().then(res => {
      setAvailableMonths(res.data?.months || res.data || []);
    }).catch(() => {});
  }, []);

  // Load generated statements when create dialog opens
  const openCreateDialog = async () => {
    setCreateOpen(true);
    setLoadingStmts(true);
    try {
      const res = await overrideAPI.statements.list({ status: 'Generated', page_size: 200 });
      setGeneratedStatements(res.data.items || []);
    } catch {
      toast.error('Failed to load statements');
    }
    setLoadingStmts(false);
  };

  const selectStatement = (stmt) => {
    setSelectedStmt(stmt);
    setCreateForm(f => ({
      ...f,
      statement_id: stmt.statement_id,
      amount: stmt.total_amount?.toFixed(2) || '',
    }));
  };

  const handleCreate = async () => {
    if (!createForm.statement_id || !createForm.payment_date || !createForm.amount) {
      toast.error('Please select a statement, date, and amount');
      return;
    }
    setCreating(true);
    try {
      await overrideAPI.payments.create({
        statement_id: createForm.statement_id,
        amount: parseFloat(createForm.amount),
        payment_date: createForm.payment_date,
        payment_method: createForm.payment_method,
        reference_number: createForm.reference_number || undefined,
        notes: createForm.notes || undefined,
      });
      toast.success('Payment recorded successfully');
      setCreateOpen(false);
      setCreateForm({ statement_id: '', payment_date: '', payment_method: 'Direct Deposit', amount: '', reference_number: '', notes: '' });
      setSelectedStmt(null);
      setStmtSearch('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment');
    }
    setCreating(false);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    setUpdatingStatus(true);
    try {
      await overrideAPI.payments.updateStatus(statusDialog.payment_id, newStatus, statusNotes || undefined);
      toast.success(`Payment marked as ${newStatus}`);
      setStatusDialog(null);
      setNewStatus('');
      setStatusNotes('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
    setUpdatingStatus(false);
  };

  const totalPages = Math.ceil(total / pageSize);
  const activeFilterCount = Object.values(filters).filter(v => v !== 'all').length;

  const filteredStmts = stmtSearch
    ? generatedStatements.filter(s => s.payee_name.toLowerCase().includes(stmtSearch.toLowerCase()) || s.statement_month.includes(stmtSearch))
    : generatedStatements;

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Accounting</p>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Payments</h1>
          <p className="text-sm text-slate-500 mt-1">Track and manage payments made against generated statements</p>
        </div>
        {canManage && (
          <Button size="sm" className="gap-1.5" onClick={openCreateDialog} data-testid="new-payment-btn">
            <Plus size={14} /> Record Payment
          </Button>
        )}
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search payee name..." className="pl-9 h-9 text-sm" data-testid="payment-search" />
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1.5" data-testid="toggle-payment-filters">
          <Filter size={14} /> Filters
          {activeFilterCount > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{activeFilterCount}</Badge>}
        </Button>
      </div>

      {showFilters && (
        <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="payment-filter-panel">
          <Select value={filters.status} onValueChange={v => { setFilters(f => ({ ...f, status: v })); setPage(1); }}>
            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {PAYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.statement_month} onValueChange={v => { setFilters(f => ({ ...f, statement_month: v })); setPage(1); }}>
            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {availableMonths.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center" data-testid="payments-empty">
              <CreditCard size={36} className="mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium text-slate-500">No payments recorded yet</p>
              <p className="text-xs text-slate-400 mt-1">Record a payment against a generated statement to get started</p>
              {canManage && (
                <Button size="sm" variant="outline" className="mt-4 gap-1.5" onClick={openCreateDialog}>
                  <Plus size={14} /> Record First Payment
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payments-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs text-slate-500 uppercase">
                    <th className="px-4 py-3">Payee</th>
                    <th className="px-4 py-3">Month</th>
                    <th className="px-4 py-3">Method</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(p => (
                    <tr key={p.payment_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{p.payee_name}</p>
                        <p className="text-xs text-slate-400">{p.payee_type}</p>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{p.statement_month}</td>
                      <td className="px-4 py-3 text-sm">{p.payment_method}</td>
                      <td className="px-4 py-3 font-mono text-xs">{p.payment_date}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.reference_number || '—'}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">{fmtMoney(p.amount)}</td>
                      <td className="px-4 py-3">
                        <Badge className={`text-xs gap-1 ${STATUS_COLORS[p.status] || ''}`}>
                          {STATUS_ICONS[p.status]} {p.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {canManage && p.status !== 'Paid' && p.status !== 'Voided' && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => { setStatusDialog(p); setNewStatus(''); setStatusNotes(''); }}
                              data-testid={`payment-update-${p.payment_id}`}>
                              <ArrowUpDown size={12} /> Update
                            </Button>
                          )}
                          {canManage && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                              title="Delete Payment"
                              onClick={async () => {
                                if (window.confirm(`Delete payment for "${p.payee_name}"?`)) {
                                  try {
                                    await overrideAPI.payments.delete(p.payment_id);
                                    toast.success('Payment deleted');
                                    loadData();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to delete payment');
                                  }
                                }
                              }}
                              data-testid={`payment-delete-${p.payment_id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm" data-testid="payment-pagination">
          <span className="text-slate-500">Page {page} of {totalPages} ({total} total)</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
              <ChevronLeft size={14} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Create Payment Dialog — Statement Selector */}
      <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) { setSelectedStmt(null); setStmtSearch(''); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Payment</DialogTitle>
            <DialogDescription>Select a generated statement and enter payment details</DialogDescription>
          </DialogHeader>

          {!selectedStmt ? (
            <div className="space-y-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={stmtSearch} onChange={e => setStmtSearch(e.target.value)}
                  placeholder="Search statements by payee or month..." className="pl-9 h-9 text-sm" data-testid="stmt-selector-search" />
              </div>
              {loadingStmts ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
              ) : filteredStmts.length === 0 ? (
                <div className="py-8 text-center">
                  <Info size={24} className="mx-auto mb-2 text-slate-300" />
                  <p className="text-sm text-slate-500">No generated statements available</p>
                  <p className="text-xs text-slate-400 mt-1">Generate a statement first to record payments</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="stmt-selector-list">
                  {filteredStmts.map(s => (
                    <button key={s.statement_id} onClick={() => selectStatement(s)}
                      className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-sky-300 hover:bg-sky-50 transition-colors"
                      data-testid={`select-stmt-${s.statement_id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{s.payee_name}</p>
                          <p className="text-xs text-slate-400">{s.payee_type} | {s.statement_month} | {s.line_count} lines</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold font-mono text-slate-900">{fmtMoney(s.total_amount)}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Selected statement summary */}
              <div className="p-3 bg-sky-50 rounded-lg border border-sky-200" data-testid="selected-stmt-summary">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{selectedStmt.payee_name}</p>
                    <p className="text-xs text-slate-500">{selectedStmt.payee_type} | {selectedStmt.statement_month}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold font-mono">{fmtMoney(selectedStmt.total_amount)}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-xs mt-1 h-6 px-2 text-sky-600" onClick={() => { setSelectedStmt(null); setCreateForm(f => ({ ...f, statement_id: '', amount: '' })); }}>
                  Change statement
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Payment Date *</Label>
                  <Input type="date" value={createForm.payment_date} onChange={e => setCreateForm(f => ({ ...f, payment_date: e.target.value }))}
                    className="h-9 text-sm" data-testid="pay-date" />
                </div>
                <div>
                  <Label className="text-xs">Method *</Label>
                  <Select value={createForm.payment_method} onValueChange={v => setCreateForm(f => ({ ...f, payment_method: v }))}>
                    <SelectTrigger className="h-9 text-sm" data-testid="pay-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Amount *</Label>
                  <Input type="number" step="0.01" value={createForm.amount} onChange={e => setCreateForm(f => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00" className="h-9 text-sm" data-testid="pay-amount" />
                </div>
                <div>
                  <Label className="text-xs">Reference #</Label>
                  <Input value={createForm.reference_number} onChange={e => setCreateForm(f => ({ ...f, reference_number: e.target.value }))}
                    placeholder="Check # or EFT ref" className="h-9 text-sm" data-testid="pay-reference" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional notes..." className="text-sm" rows={2} data-testid="pay-notes" />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateOpen(false); setSelectedStmt(null); setStmtSearch(''); }}>Cancel</Button>
            {selectedStmt && (
              <Button onClick={handleCreate} disabled={creating} data-testid="confirm-create-payment-btn">
                {creating ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Record Payment
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="update-status-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Update Payment Status</DialogTitle>
            <DialogDescription>Change the status of this payment record</DialogDescription>
          </DialogHeader>
          {statusDialog && (
            <div className="space-y-3">
              <div className="text-sm bg-slate-50 rounded-lg p-3 border">
                <p><span className="text-slate-500">Payee:</span> <strong>{statusDialog.payee_name}</strong></p>
                <p><span className="text-slate-500">Amount:</span> <span className="font-mono">{fmtMoney(statusDialog.amount)}</span></p>
                <p><span className="text-slate-500">Current:</span> <Badge className={`text-xs ${STATUS_COLORS[statusDialog.status]}`}>{statusDialog.status}</Badge></p>
              </div>
              <div>
                <Label className="text-xs">New Status *</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="h-9 text-sm" data-testid="new-status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUSES.filter(s => s !== statusDialog.status).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={statusNotes} onChange={e => setStatusNotes(e.target.value)}
                  placeholder="Optional notes..." className="text-sm" rows={2} data-testid="status-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)}>Cancel</Button>
            <Button onClick={handleStatusUpdate} disabled={updatingStatus || !newStatus} data-testid="confirm-status-update-btn">
              {updatingStatus ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
